import React, { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
} from "@mui/material";

import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import HeadsetMicRoundedIcon from "@mui/icons-material/HeadsetMicRounded";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import LibraryBooksRoundedIcon from "@mui/icons-material/LibraryBooksRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function Contact() {
  const { style } = useStyle();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <>
      <Seo
        title="Contact | Parish Audio"
        description="Get in touch with Parish Audio for plugin support, product questions, and general inquiries."
        canonical="https://parishaudio.com/contact"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 980,
            mx: "auto",
            minWidth: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              minWidth: 0,
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2.25, md: 3 },
                backgroundColor: style.darkPanel,
                borderBottom: `1px solid ${style.color2}`,
              }}
            >
              <Stack spacing={1} alignItems="flex-start">
                <Chip
                  icon={<HeadsetMicRoundedIcon />}
                  label="Contact"
                  sx={{
                    borderRadius: "5px",
                    fontWeight: 700,
                    color: style.color4,
                    backgroundColor: "rgba(255,255,255,0.06)",
                  }}
                />

                <Typography
                  variant="h1"
                  sx={{
                    color: style.color4,
                    fontSize: { xs: "2rem", md: "2.7rem" },
                    fontWeight: 800,
                    lineHeight: 1.05,
                    letterSpacing: "-0.03em",
                  }}
                >
                  Get in touch
                </Typography>

                <Typography
                  sx={{
                    color: style.color3,
                    maxWidth: 700,
                    lineHeight: 1.7,
                    fontSize: { xs: "1rem", md: "1.05rem" },
                  }}
                >
                  Questions about a plugin, downloads, or your account? We’re happy to help.
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
              <Stack spacing={2}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "16px",
                    backgroundColor: style.darkPanel,
                    border: "1px solid rgba(255,255,255,0.08)",
                    p: { xs: 1.5, md: 2 },
                  }}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmailRoundedIcon sx={{ color: style.color3 }} />
                      <Typography
                        sx={{
                          color: style.color4,
                          fontWeight: 800,
                          fontSize: "1.1rem",
                        }}
                      >
                        Email Support
                      </Typography>
                    </Stack>

                    <Typography
                      sx={{
                        color: style.color3,
                        lineHeight: 1.7,
                      }}
                    >
                      For support, product questions, or general inquiries, email us at:
                    </Typography>

                    <Button
                      component="a"
                      href="mailto:support@parishaudio.com"
                      startIcon={<EmailRoundedIcon />}
                      sx={{
                        alignSelf: "flex-start",
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: style.accentGreen,
                        px: 2,
                        "&:hover": {
                          backgroundColor: style.accentGreen,
                          filter: "brightness(1.05)",
                        },
                      }}
                    >
                      support@parishaudio.com
                    </Button>
                  </Stack>
                </Paper>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "16px",
                      backgroundColor: style.darkPanel,
                      border: "1px solid rgba(255,255,255,0.08)",
                      p: { xs: 1.5, md: 2 },
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <QuestionAnswerRoundedIcon sx={{ color: style.color3 }} />
                        <Typography
                          sx={{
                            color: style.color4,
                            fontWeight: 800,
                            fontSize: "1.02rem",
                          }}
                        >
                          Need help fast?
                        </Typography>
                      </Stack>

                      <Typography
                        sx={{
                          color: style.color3,
                          lineHeight: 1.65,
                        }}
                      >
                        Include as much detail as you can, such as the plugin name, your operating system, and what issue you’re seeing.
                      </Typography>
                    </Stack>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "16px",
                      backgroundColor: style.darkPanel,
                      border: "1px solid rgba(255,255,255,0.08)",
                      p: { xs: 1.5, md: 2 },
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LibraryBooksRoundedIcon sx={{ color: style.color3 }} />
                        <Typography
                          sx={{
                            color: style.color4,
                            fontWeight: 800,
                            fontSize: "1.02rem",
                          }}
                        >
                          Looking for guides?
                        </Typography>
                      </Stack>

                      <Typography
                        sx={{
                          color: style.color3,
                          lineHeight: 1.65,
                        }}
                      >
                        You can also explore the Learning Center for plugin tips, audio concepts, and workflow guidance.
                      </Typography>

                      <Button
                        component={RouterLink}
                        to="/learning"
                        endIcon={<ArrowForwardRoundedIcon />}
                        sx={{
                          alignSelf: "flex-start",
                          mt: 0.5,
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.10)",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.10)",
                          },
                        }}
                      >
                        Visit Learning Center
                      </Button>
                    </Stack>
                  </Paper>
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}